import type { ComponentProps } from "react"
import { useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Placeholder from "@/v2-ui/Placeholder"
import Button from "@/v2-backoffice-client/components/button"
import UptimeConfigProviderDialog from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigProviderDialog"
import UptimeConfigProviderRow from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigProviderRow"
import UptimeConfigHostSuffixDialog from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigHostSuffixDialog"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"

type Provider = UptimeConfigQuery["getStatusConfig"]["Providers"][number]

export type UptimeConfigProvidersTabProps = ComponentProps<"div"> & {
  providers: Provider[]
}

// UptimeConfigProvidersTab edits the host-suffix -> provider
// directory the public status page uses to render the "Provided
// by ..." label on each service. The host-suffix sub-rows are
// CRUD'd inline because they're very small and tied 1:1 to the
// owning provider.
function UptimeConfigProvidersTab(props: UptimeConfigProvidersTabProps) {
  const { providers, className, ...divProps } = props
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null)
  const [isCreateProviderOpen, setIsCreateProviderOpen] = useState(false)
  const [hostSuffixContext, setHostSuffixContext] = useState<{
    providerId: string
    existing: Provider["HostSuffixes"][number] | null
  } | null>(null)

  const sorted = useMemo(() => {
    return [...providers].toSorted(
      (a, b) => a.Priority - b.Priority || a.Name.localeCompare(b.Name),
    )
  }, [providers])

  return (
    <div className={cn("uptime-config-tab", className)} {...divProps}>
      <div className="uptime-config-tab-header">
        <div className="uptime-config-tab-header-text">
          <h3 className="uptime-config-tab-title">Providers</h3>
          <p className="uptime-config-tab-description">
            Maps host suffixes to the vendor name shown on the public status
            page (most-specific suffix wins on ties).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconSrc="plus"
          onClick={() => {
            setIsCreateProviderOpen(true)
          }}
        >
          New provider
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Placeholder className="py-8">
          No providers configured. Add one and link host suffixes underneath.
        </Placeholder>
      ) : (
        <ul className="uptime-config-list">
          {sorted.map((p) => (
            <UptimeConfigProviderRow
              key={p.Id}
              provider={p}
              onEdit={() => {
                setEditingProvider(p)
              }}
              onAddHostSuffix={() => {
                setHostSuffixContext({ providerId: p.Id, existing: null })
              }}
              onEditHostSuffix={(hs) => {
                setHostSuffixContext({ providerId: p.Id, existing: hs })
              }}
            />
          ))}
        </ul>
      )}

      <UptimeConfigProviderDialog
        open={isCreateProviderOpen}
        onOpenChange={setIsCreateProviderOpen}
        mode="create"
      />
      <UptimeConfigProviderDialog
        open={editingProvider !== null}
        onOpenChange={(o) => {
          if (!o) setEditingProvider(null)
        }}
        mode="edit"
        provider={editingProvider}
      />
      <UptimeConfigHostSuffixDialog
        open={hostSuffixContext !== null}
        onOpenChange={(o) => {
          if (!o) setHostSuffixContext(null)
        }}
        mode={hostSuffixContext?.existing ? "edit" : "create"}
        providerId={hostSuffixContext?.providerId ?? ""}
        hostSuffix={hostSuffixContext?.existing ?? null}
      />
    </div>
  )
}

export default UptimeConfigProvidersTab
